// Player noise & visibility model + the world's noise emitter
// (INFECTED.md Z20–Z28, plus the feeder requirements Z117–Z124).
//
// The rule that makes the infected readable: NOISE IS A RADIUS IN METRES.
// `noiseRadius()` returns how far away the player can be heard right now, and
// the infected hears you if it is inside that circle — no hidden curves, no
// per-entity perception rolls. `visibility()` is DayZ's own sight formula,
// returned as a multiplier on the infected's 60 m base sight range.
//
// Both are PURE functions of the player state plus the two world hooks that
// already exist in main.js (`world.surfaceAt`, `world.noiseFactor`), so a HUD
// noise meter can be added later without touching the simulation (Z23).
// Nothing else in the codebase may re-implement this maths.
//
// On top of the two pure functions sits `NoiseSystem`: the single emitter the
// whole game reports one-shot noises into (gunshots, doors, breaking glass, a
// corpse being rummaged). It rate-limits, sanity-clamps, hands the ping to any
// local listener (the `?solo=1` in-browser InfectedSim) and forwards it to the
// server as `{k:'noise', x, z, r}` (Z27). The module has ZERO imports so
// `server/*.mjs` and node harnesses can import it for the same clamp.

// stance × speed class → metres (Z20)
const BASE = {
  stand: { idle: 0, walk: 10, run: 18, sprint: 28 },
  crouch: { idle: 0, walk: 6, run: 12, sprint: 12 },
  prone: { idle: 0, walk: 4, run: 4, sprint: 4 },
};

// what you are walking on (Z21) — looting a house on floorboards is loud
const SURFACE = {
  grass: 0.8, forest: 0.7, sand: 0.8, snow: 0.9, dirt: 1.0,
  asphalt: 1.25, wood: 1.35, metal: 1.5, water: 1.4,
};

// sky mood → how far you can be SEEN (Z24)
const LIGHT = { clear: 1.0, partly: 1.0, overcast: 0.85, misty: 0.7, sunset: 0.7, night: 0.3 };

export const MAX_RADIUS = 45;   // Z21 clamp on the continuous footprint
export const SNEAK_MUL = 0.35;  // Ctrl-sneak: the controller already near-mutes the step

export function speedClass(speed) {
  if (!(speed > 0)) return 'idle';
  if (speed < 0.15) return 'idle';
  if (speed < 2.2) return 'walk';
  if (speed < 5.5) return 'run';
  return 'sprint';
}

// s: { x, z, stance, speed, torch, sneak }  world: { surfaceAt, noiseFactor }
export function noiseRadius(s, world) {
  const base = (BASE[s.stance] || BASE.stand)[speedClass(s.speed)] || 0;
  if (base <= 0) return 0;
  const surf = world?.surfaceAt ? SURFACE[world.surfaceAt(s.x, s.z)] ?? 1 : 1;
  const factor = world?.noiseFactor ? world.noiseFactor(s.x, s.z) : 1;
  const sneak = s.sneak ? SNEAK_MUL : 1;
  return Math.round(Math.max(0, Math.min(MAX_RADIUS, base * surf * factor * sneak)));
}

// v = ((speedCoef + stanceCoef) / 2) * lightCoef  (Z24)
// `mood` accepts the key, or sky.mood itself ({ key, … }).
export function visibility(s, mood = 'overcast') {
  const key = typeof mood === 'string' ? mood : mood?.key || 'overcast';
  const speedCoef = { idle: 0.3, walk: 0.66, run: 0.85, sprint: 1.0 }[speedClass(s.speed)];
  const stanceCoef = { stand: 1.5, crouch: 0.6, prone: 0.15 }[s.stance] ?? 1.5;
  // a lit torch at night is a beacon, not camouflage
  const light = (LIGHT[key] ?? 0.85) + (s.torch ? 0.9 : 0);
  return Math.max(0.05, Math.min(2, ((speedCoef + stanceCoef) / 2) * light));
}

// the two scalars the 10 Hz player state carries (Z22)
export function senseState(s, world) {
  return { n: noiseRadius(s, world), v: +visibility(s, s.mood).toFixed(2) };
}

// One-shot noise pings (Z27) — radius in metres per event kind.
export const PING = {
  rifle: 250, pistol: 180, suppressed: 90, scream: 60, glass: 35,
  campfire: 25, melee: 22, door: 20, vault: 12, rustle: 6,
};

export const PING_MAX = 250;    // nothing in the world is louder than a rifle
export const PING_TTL = 3;      // s a ping stays queryable in the local ring
export const WORLD_HALF = 5200; // 10 km map + slack; coords beyond are nonsense

// ---- Z121: server-side sanity clamp -------------------------------------
// mp-server.mjs relays `{k:'noise'}` like `swing`/`shot` — never persisted —
// but a client may not invent a 9 km scream. Returns a clean event, or null
// if the payload is junk.
export function sanitizePing(ev) {
  if (!ev || ev.k !== 'noise') return null;
  const x = Number(ev.x), z = Number(ev.z), r = Number(ev.r);
  if (!Number.isFinite(x) || !Number.isFinite(z) || !Number.isFinite(r)) return null;
  if (Math.abs(x) > WORLD_HALF || Math.abs(z) > WORLD_HALF) return null;
  const rr = Math.round(Math.max(0, Math.min(PING_MAX, r)));
  if (rr <= 0) return null;
  const out = { k: 'noise', x: +x.toFixed(1), z: +z.toFixed(1), r: rr };
  if (typeof ev.why === 'string') out.why = ev.why.slice(0, 12);
  return out;
}

// ---- the emitter ---------------------------------------------------------
// One instance per session, created in main.js as `world.noise`. Everything
// that makes a sound reports here; listeners (the infected sim) read here.
// Nothing else in the game may build a `{k:'noise'}` object by hand.
export class NoiseSystem {
  constructor(world = null, opts = {}) {
    this.world = world;
    this.sink = opts.sink || null;   // (ev) => net.event(ev)
    this.listeners = [];             // local sims (?solo=1)
    this.events = [];                // recent pings: { x, z, r, why, t }
    this.muted = false;
    this.maxPerSecond = opts.maxPerSecond || 12;
    this._bucket = this.maxPerSecond;
    this._bucketT = 0;
    this._last = new Map();          // why → { t, x, z }
    this.now = opts.now || (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
    this.stats = { emitted: 0, dropped: 0 };
  }

  // a local consumer of pings (the solo InfectedSim mirrors what the server
  // would have relayed). Returns an unsubscribe function.
  listen(fn) {
    this.listeners.push(fn);
    return () => { const i = this.listeners.indexOf(fn); if (i >= 0) this.listeners.splice(i, 1); };
  }

  // Z27: emit one loud one-shot. `why` keys PING; `opts.r` overrides it,
  // `opts.mul` scales it. Returns the emitted event, or null if suppressed.
  emit(why, x, z, opts = {}) {
    if (this.muted) return null;
    const t = this.now();
    // token bucket: a stuck call site can never flood the socket
    const dt = (t - this._bucketT) / 1000;
    this._bucketT = t;
    this._bucket = Math.min(this.maxPerSecond, this._bucket + dt * this.maxPerSecond);
    // de-dupe: the same kind of noise from the same spot inside 120 ms is one
    const prev = this._last.get(why);
    if (prev && t - prev.t < 120 && Math.hypot(prev.x - x, prev.z - z) < 3) { this.stats.dropped++; return null; }
    if (this._bucket < 1) { this.stats.dropped++; return null; }
    const r = (opts.r ?? PING[why] ?? 0) * (opts.mul ?? 1);
    const ev = sanitizePing({ k: 'noise', x, z, r, why });
    if (!ev) return null;
    this._bucket -= 1;
    this._last.set(why, { t, x, z });
    this.stats.emitted++;
    this.events.push({ x: ev.x, z: ev.z, r: ev.r, why, t });
    if (this.events.length > 64) this.events.shift();
    for (const fn of this.listeners) { try { fn(ev); } catch { /* a deaf listener is not fatal */ } }
    this.sink?.(ev);
    return ev;
  }

  // convenience wrappers for the call sites listed in INFECTED.md §11 (Z118)
  gunshot(weapon, x, z, suppressed = false) {
    return this.emit(suppressed ? 'suppressed' : weapon === 'pistol' ? 'pistol' : 'rifle', x, z);
  }
  melee(x, z, hitFlesh = true) { return hitFlesh ? this.emit('melee', x, z) : this.emit('vault', x, z, { r: 8 }); }
  door(x, z) { return this.emit('door', x, z); }
  glass(x, z) { return this.emit('glass', x, z); }
  campfire(x, z) { return this.emit('campfire', x, z); }
  vault(x, z) { return this.emit('vault', x, z); }
  rustle(x, z) { return this.emit('rustle', x, z); }
  scream(x, z) { return this.emit('scream', x, z); }

  // ---- queries ----------------------------------------------------------
  // the continuous player footprint (Z20) and the pair of state scalars (Z22)
  playerRadius(s) { return noiseRadius(s, this.world); }
  playerSenses(s) { return senseState(s, this.world); }

  // drop pings older than the ttl
  prune(t = this.now()) {
    const cut = t - PING_TTL * 1000;
    while (this.events.length && this.events[0].t < cut) this.events.shift();
    return this.events.length;
  }

  // loudest live ping heard at (x, z): { r, d, why, loud, x, z } or null.
  // `loud` decays with distance AND with age — a bang you heard two seconds
  // ago is a memory, not a stimulus.
  loudestAt(x, z, t = this.now()) {
    this.prune(t);
    let best = null;
    for (const e of this.events) {
      const d = Math.hypot(e.x - x, e.z - z);
      if (d > e.r) continue;
      const age = (t - e.t) / 1000;
      const loud = (1 - d / e.r) * Math.max(0, 1 - age / PING_TTL);
      if (!best || loud > best.loud) best = { r: e.r, d, why: e.why, loud, x: e.x, z: e.z };
    }
    return best;
  }

  // does a listener standing at (x, z) hear the player's continuous noise?
  hears(x, z, s) {
    const r = this.playerRadius(s);
    if (r <= 0) return false;
    return Math.hypot((s.x || 0) - x, (s.z || 0) - z) <= r;
  }
}

export default NoiseSystem;
